import React, { useState } from 'react';

import { useShortlistState } from '#states/hooks.js';

import { Button } from '../button.js';
import { Checkbox } from '../checkbox.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../dialog.js';

interface SelectGroupsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (groupIds: string[]) => void;
  title: string;
  description?: string;
  /** Mode: 'add' shows all groups, 'remove' shows only groups containing the item */
  mode?: 'add' | 'remove';
  /** Pre-selected group IDs (for remove mode, these are the groups the item is currently in) */
  preselectedGroupIds?: string[];
  /** Groups to show (for remove mode, only show groups that contain the item) */
  groupsToShow?: string[];
}

export function SelectGroupsDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  mode = 'add',
  preselectedGroupIds = [],
  groupsToShow,
}: SelectGroupsDialogProps) {
  const shortlistState = useShortlistState();
  const allGroups = shortlistState.groups;
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  // Memoize groups to display to prevent unnecessary recalculations
  const groups = React.useMemo(() => {
    if (groupsToShow && groupsToShow.length > 0) {
      return allGroups.filter((g) => groupsToShow.includes(g.id));
    }
    return allGroups;
  }, [allGroups, groupsToShow]);

  // Initialize selected groups when dialog opens, reset when it closes
  // Use a stringified version of preselectedGroupIds to avoid infinite loops
  const preselectedGroupIdsKey = preselectedGroupIds?.join(',') ?? '';

  React.useEffect(() => {
    if (open) {
      if (preselectedGroupIds && preselectedGroupIds.length > 0) {
        setSelectedGroupIds(new Set(preselectedGroupIds));
      } else {
        setSelectedGroupIds(new Set());
      }
    } else {
      // Reset when dialog closes
      setSelectedGroupIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectedGroupIdsKey]);

  const handleToggleGroup = (groupId: string) => {
    const newSelected = new Set(selectedGroupIds);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroupIds(newSelected);
  };

  const handleConfirm = () => {
    if (selectedGroupIds.size > 0) {
      onConfirm([...selectedGroupIds]);
      setSelectedGroupIds(new Set());
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedGroupIds(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div>
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No shortlists available.</p>
          ) : (
            <div className="-mx-3 max-h-[300px] overflow-y-auto">
              {groups.map((group) => (
                <label
                  key={group.id}
                  className="flex items-center gap-3 p-3 rounded-md hover:bg-secondary hover:text-secondary-foreground cursor-pointer"
                >
                  <Checkbox
                    checked={selectedGroupIds.has(group.id)}
                    onCheckedChange={() => handleToggleGroup(group.id)}
                  />
                  <span className="flex-1 text-sm font-medium">{group.displayName}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedGroupIds.size === 0}>
            {mode === 'add' ? (
              <>
                Add to {selectedGroupIds.size > 0 ? `${selectedGroupIds.size} ` : ''}shortlist
                {selectedGroupIds.size !== 1 ? 's' : ''}
              </>
            ) : (
              <>
                Remove from {selectedGroupIds.size > 0 ? `${selectedGroupIds.size} ` : ''}shortlist
                {selectedGroupIds.size !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
